import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { EmailStateManager } from '@/hooks/email/useEmailManager';
import { AuthStateManager } from '@/hooks/auth/useAuthManager';
import { clearUserCacheForOrg } from '@/hooks/auth/useCurrentUser';
import { logDiag } from '@/utils/appDiagnostics';
import { resolverOrgAtual, type ResultadoBuscaOrgs } from './resolverOrgAtual';

export const CHAVE_ORG_GUARDADA = 'current_org_id';

// Esperas entre novas tentativas quando a busca de orgs falha. Cobre o caso comum de falha
// passageira (requisição abortada, refresh de JWT, 5xx isolado) sem prender o boot: no pior caso
// são ~4 s a mais, abaixo do timeout de segurança de 10 s do init.
const ESPERAS_NOVA_TENTATIVA_MS = [1000, 3000];

const dormir = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// O erro do PostgREST é objeto simples com `message`, não instância de Error.
const mensagemDeErro = (error: unknown): string => {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
};

export interface Organization {
  id: string;
  name: string;
  role: string;
  goal_metrics_enabled: boolean;
  /** Kill-switch do modulo de comissionamento. Nasce false — ver 20260920100000. */
  commissions_enabled: boolean;
  /**
   * Kill-switch da sincronizacao com o app de Fluxo de Caixa — ver 20260920101000.
   * Separado de `commissions_enabled` de proposito: usar o modulo e mandar comissao para outro
   * sistema que lanca conta a pagar sao decisoes diferentes, e a segunda e mais grave.
   */
  commissions_fcx_sync_enabled: boolean;
  commissions_receivable_enabled: boolean;
  /** Kill-switch do modulo de Projetos (Operacoes). Nasce false — ver 20260921100000. */
  projects_enabled: boolean;
  /**
   * Kill-switch da aba Comunicação da Carteira. Nasce false — ver 20260923120000. Esconde a
   * aba; a visibilidade das conversas restritas NÃO depende dela.
   */
  contract_comm_enabled: boolean;
  /**
   * Kill-switch do ciclo de vida dos contratos (estender, pausar, renegociar, expandir, esteira
   * de cancelamento). Nasce false (20260929171806); as RPCs `contract_*` também recusam.
   */
  contract_lifecycle_enabled: boolean;
  /**
   * Kill-switch da área Materiais › Playbooks. Nasce false (20261003200000); a RLS das tabelas
   * `playbook*` também nega tudo com a flag desligada.
   */
  playbooks_enabled: boolean;
  /**
   * Kill-switch do resumo por IA na tela de transcrição de reunião. Nasce false: ver
   * 20260930160001 e docs/specs/transcricao-reunioes-v2.md.
   */
  meeting_ai_enabled: boolean;
  /**
   * Kill-switch do resumo, dos momentos-chave e das perguntas por IA na tela de ligação.
   * Ver 20261002204251 e docs/specs/ligacao-resumo-ia.md.
   */
  call_ai_enabled: boolean;
  /**
   * Kill-switch das cobranças da Kamino na aba Financeiro da Carteira. Nasce false: ver
   * 20261003190000 e docs/specs/contrato-saude-por-ia.md. Ver as cobranças exige também a
   * chave `operacoes.carteira.financeiro` (a RLS checa as duas coisas).
   */
  contract_kamino_sync_enabled: boolean;
  /**
   * Kill-switch das sugestões de saúde do contrato pela IA na Carteira. Nasce false: ver
   * 20261004140000 e docs/specs/contrato-saude-por-ia.md (Fase 2). A RLS também checa.
   */
  contract_health_ai_enabled: boolean;
  /**
   * Preset de interface (tema + modulos visiveis) — ver 20260930100000 e src/lib/orgPresets.ts.
   * `null` = Vertto padrao. Tipado como string solta de proposito: um valor que este build
   * ainda nao conhece cai em `getOrgPreset` e vira `null`, nunca um preset pela metade.
   */
  ui_preset: string | null;
}

export interface OrganizationContextType {
  currentOrgId: string | null;
  currentOrg: Organization | null;
  availableOrgs: Organization[];
  switchOrganization: (orgId: string) => Promise<void>;
  loading: boolean;
  error: string | null;
  refetchOrganizations: () => Promise<void>;
}

// Exportado para que o console Super Admin possa SOBRESCREVER a org corrente ao abrir a página
// de uma organização (`OrgScopeProvider`). Sem isso, a alternativa seria passar `orgId` por prop
// para os componentes de aba — o que significaria parametrizar os ~6 hooks que chamam
// `useCurrentOrgId()` internamente, e o modo de falha não é erro: é editar a empresa errada.
export const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

export const OrganizationProvider = ({ children }: OrganizationProviderProps) => {
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [availableOrgs, setAvailableOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const hasInitializedRef = useRef(false);
  // Org resolvida mais recente, lida dentro de `loadCurrentOrganization` (um useCallback sem
  // `currentOrg` nas deps veria o valor velho).
  const orgResolvidaRef = useRef<Organization | null>(null);

  // Devolve `{ ok: false }` em QUALQUER falha, em vez de uma lista vazia. Lista vazia significa
  // "o usuário não tem org" e apaga a org guardada; falha significa "não sei agora" e não pode
  // apagar nada. Ver `resolverOrgAtual.ts`.
  const fetchAvailableOrganizations = useCallback(async (): Promise<ResultadoBuscaOrgs<Organization>> => {
    try {
      // Usar getSession() primeiro (mais rápido que getUser())
      const { data: sessionData } = await supabase.auth.getSession();

      let userId: string | null = null;

      if (sessionData?.session?.user) {
        userId = sessionData.session.user.id;
      } else {
        // Fallback para getUser() se não houver sessão. Sem sessão local o getUser responde
        // "Auth session missing": é gente deslogada, não falha de busca, então segue como lista
        // vazia (o comportamento de sempre).
        const { data: authData } = await supabase.auth.getUser();
        if (!authData?.user) {
          setAvailableOrgs([]);
          return { ok: true, orgs: [] };
        }
        userId = authData.user.id;
      }

      if (!userId) {
        setAvailableOrgs([]);
        return { ok: true, orgs: [] };
      }

      const PAGE_SIZE = 100;
      let allMemberships: any[] = [];
      let from = 0;

      // ── Colunas de FLAG são carregadas de forma DEGRADÁVEL ──
      //
      // Este select é a raiz do app: se ele falha, `organizations` vem vazio, nenhuma org é
      // resolvida e o usuário cai em "Sem acesso" — o CRM inteiro, não só a feature da flag.
      //
      // E ele falha inteiro por UMA coluna desconhecida: o PostgREST responde 42703 para a query
      // toda. Isso acontece de verdade sempre que o ambiente está atrás da migration — outro
      // projeto no `.env`, um deploy de front antes do DDL, ou o cache de schema do PostgREST
      // ainda não recarregado. Aconteceu em 2026-09-07 com `commissions_receivable_enabled`.
      //
      // Então: tenta com as flags; se o erro for "coluna não existe", refaz SEM elas e segue com
      // as flags em `false`. A página nova some do menu (que é o comportamento certo para um
      // ambiente que ainda não tem a coluna) e o resto do CRM continua de pé.
      const COLUNAS_FLAG = [
        'goal_metrics_enabled',
        'commissions_enabled',
        'commissions_fcx_sync_enabled',
        'commissions_receivable_enabled',
        'projects_enabled',
        'contract_comm_enabled',
        'contract_lifecycle_enabled',
        'playbooks_enabled',
        'meeting_ai_enabled',
        'call_ai_enabled',
        'contract_kamino_sync_enabled',
        'contract_health_ai_enabled',
        'ui_preset',
      ];
      let colunasOrg = ['id', 'name', ...COLUNAS_FLAG];

      while (true) {
        const consulta = () => supabase
          .from('memberships')
          .select(`
            org_id,
            role,
            organizations!inner(${colunasOrg.join(',')})
          `)
          .eq('user_id', userId)
          .eq('status', 'active')
          // ORDER BY estável é obrigatório: paginação por OFFSET (.range) sem ordem
          // determinística pode pular/duplicar linhas entre páginas. Para usuários com
          // >100 orgs isso fazia a org em cache (ex.: Midhaus) sumir da lista e o
          // resolver cair no fallback orgs[0] (org arbitrária). org_id é único por
          // usuário (PK memberships = org_id+user_id) → ordem total estável.
          .order('org_id', { ascending: true })
          .range(from, from + PAGE_SIZE - 1);

        let { data, error } = await consulta();

        // 42703 = undefined_column. Só este código dispara o fallback: qualquer outro erro
        // (rede, RLS, timeout) continua subindo, porque mascará-los esconderia falha real.
        if (error && (error as { code?: string }).code === '42703' && colunasOrg.length > 2) {
          console.warn(
            '[OrganizationContext] coluna de flag ausente neste ambiente — recarregando sem as flags.',
            (error as { message?: string }).message,
          );
          colunasOrg = ['id', 'name'];
          ({ data, error } = await consulta());
        }

        if (error) throw error;
        if (data && data.length > 0) allMemberships = allMemberships.concat(data);
        if (!data || data.length < PAGE_SIZE) break;
        from += PAGE_SIZE;
      }

      // A linha embutida de `organizations` nao esta no types.ts gerado (que segue milhares de
      // linhas atras do banco), entao a forma e declarada aqui em vez de espalhar `as any` por
      // campo — cada `as any` novo e uma chance a mais de ler uma coluna que nao existe.
      type OrgRow = {
        name: string;
        goal_metrics_enabled?: boolean | null;
        commissions_enabled?: boolean | null;
        commissions_fcx_sync_enabled?: boolean | null;
        commissions_receivable_enabled?: boolean | null;
        projects_enabled?: boolean | null;
        contract_comm_enabled?: boolean | null;
        contract_lifecycle_enabled?: boolean | null;
        playbooks_enabled?: boolean | null;
        meeting_ai_enabled?: boolean | null;
        call_ai_enabled?: boolean | null;
        contract_kamino_sync_enabled?: boolean | null;
        contract_health_ai_enabled?: boolean | null;
        ui_preset?: string | null;
      };

      const orgs: Organization[] = allMemberships.map(m => {
        const org = m.organizations as unknown as OrgRow;
        return {
          id: m.org_id,
          name: org.name,
          role: m.role,
          goal_metrics_enabled: org.goal_metrics_enabled ?? true,
          // Default false: a coluna so existe apos 20260920100000, e o modulo nasce
          // desligado. `?? true` aqui abriria a pagina em toda org antes do rollout.
          commissions_enabled: org.commissions_enabled ?? false,
          // Idem para a sincronizacao (20260920101000), que e ainda mais grave: ligada por
          // engano, manda comissao para um sistema que lanca conta a pagar.
          commissions_fcx_sync_enabled: org.commissions_fcx_sync_enabled ?? false,
          commissions_receivable_enabled: org.commissions_receivable_enabled ?? false,
          // Default false pelo mesmo motivo: a coluna so existe apos 20260921100000, e o
          // modulo nasce desligado (AGENTS.md §8 regra 8).
          projects_enabled: org.projects_enabled ?? false,
          contract_comm_enabled: org.contract_comm_enabled ?? false,
          contract_lifecycle_enabled: org.contract_lifecycle_enabled ?? false,
          playbooks_enabled: org.playbooks_enabled ?? false,
          meeting_ai_enabled: org.meeting_ai_enabled ?? false,
          call_ai_enabled: org.call_ai_enabled ?? false,
          contract_kamino_sync_enabled: org.contract_kamino_sync_enabled ?? false,
          contract_health_ai_enabled: org.contract_health_ai_enabled ?? false,
          ui_preset: org.ui_preset ?? null,
        };
      });

      setAvailableOrgs(orgs);
      return { ok: true, orgs };
    } catch (error: unknown) {
      console.error('Error fetching organizations:', error);
      return { ok: false, erro: mensagemDeErro(error) };
    }
  }, []);

  const loadCurrentOrganization = useCallback(async (forceLoading = false) => {
    // IMPORTANTE: Não setar loading=true se já foi inicializado (a menos que forceLoading=true)
    // Isso evita remontagem de componentes quando a organização é recarregada
    const isFirstLoad = !hasInitializedRef.current;
    if (isFirstLoad || forceLoading) {
      setLoading(true);
    }
    
    try {
      const orgGuardadaId = localStorage.getItem(CHAVE_ORG_GUARDADA);

      let resultado = await fetchAvailableOrganizations();
      for (const espera of ESPERAS_NOVA_TENTATIVA_MS) {
        if (resultado.ok) break;
        await dormir(espera);
        resultado = await fetchAvailableOrganizations();
      }

      const decisao = resolverOrgAtual(orgGuardadaId, resultado);

      if (decisao.tipo === 'manter') {
        // A busca falhou mesmo depois das novas tentativas. A org guardada FICA: apagá-la aqui
        // é o que jogava o usuário numa org arbitrária na carga seguinte. Se já há uma org
        // resolvida (recarga em segundo plano), o app segue nela sem alarde; se é o boot e não
        // há nenhuma, avisa, porque o app vai abrir sem org.
        console.warn('[OrganizationContext] falha ao buscar organizações; org guardada preservada.', decisao.erro);
        if (!orgResolvidaRef.current) {
          setError(decisao.erro);
          toast({
            title: 'Não foi possível carregar suas organizações',
            description: 'Verifique a conexão e recarregue a página.',
            variant: 'destructive',
          });
        }
        return;
      }

      setError(null);

      if (decisao.tipo === 'sem-org') {
        console.warn('Nenhuma organização disponível - limpando cache');
        orgResolvidaRef.current = null;
        setCurrentOrgId(null);
        setCurrentOrg(null);
        localStorage.removeItem(CHAVE_ORG_GUARDADA);
        return;
      }

      if (decisao.descartouGuardada) {
        console.warn('Organização guardada não está mais entre as do usuário - usando a primeira disponível');
      }
      orgResolvidaRef.current = decisao.org;
      setCurrentOrgId(decisao.org.id);
      setCurrentOrg(decisao.org);
      localStorage.setItem(CHAVE_ORG_GUARDADA, decisao.org.id);
    } catch (error: unknown) {
      // Só erro inesperado chega aqui (a busca não lança mais). A org guardada fica: sem uma
      // lista que carregou, não há como saber que ela deixou de valer.
      console.error('Error loading current organization:', error);
      setError(mensagemDeErro(error));
    } finally {
      setLoading(false);
      hasInitializedRef.current = true;
    }
  }, [fetchAvailableOrganizations]);

  const switchOrganization = useCallback(async (orgId: string) => {
    try {
      const currentOrgIdBefore = currentOrgId;
      // Validate that user has access to this organization
      const targetOrg = availableOrgs.find(org => org.id === orgId);
      if (!targetOrg) {
        throw new Error('Organização não encontrada ou sem permissão');
      }

      // Se for a mesma organização, não fazer nada
      if (currentOrgIdBefore === orgId) return;

      // NÃO atualizar estado do React aqui: setCurrentOrgId/setCurrentOrg causam re-render
      // em cascata (dropdown, sidebar, queries) e podem gerar erro insertBefore no portal
      // ao desmontar. Só persistir no localStorage e recarregar; após reload a org é
      // lida do localStorage em loadCurrentOrganization.
      localStorage.setItem(CHAVE_ORG_GUARDADA, orgId);

      // Limpar todo o sessionStorage (conversa selecionada em /chat, filtros, drafts,
      // openActivityId, lead-import-state, etc. carregam IDs da org anterior).
      // Preservar apenas as chaves anti-loop de reload de chunk (vite/lazyWithRetry).
      try {
        const preserveKeys = [
          'module_reload_attempted',
          'module_reload_timestamp',
          'vite_preload_reload_at',
        ];
        const preserved: Record<string, string> = {};
        for (const k of preserveKeys) {
          const v = sessionStorage.getItem(k);
          if (v !== null) preserved[k] = v;
        }
        sessionStorage.clear();
        for (const [k, v] of Object.entries(preserved)) {
          sessionStorage.setItem(k, v);
        }
      } catch (error) {
        console.error('Error clearing sessionStorage:', error);
      }

      // Limpar todos os caches antes do refresh
      try {
        const emailManager = EmailStateManager.getInstance();
        emailManager.resetAll();
        const authManager = AuthStateManager.getInstance();
        authManager.clearCache();
        if (currentOrgIdBefore) {
          clearUserCacheForOrg(currentOrgIdBefore);
        }
        queryClient.clear();
      } catch (error) {
        console.error('Error clearing caches:', error);
      }

      toast({
        title: 'Organização alterada',
        description: `Recarregando dados de ${targetOrg.name}...`,
      });

      // Redirecionar para /dashboard ao trocar de organização: evita que páginas
      // como Atendimento/Deal mantenham dados da org anterior. O full-page load
      // também evita o conflito DOM (insertBefore) ao trocar para certas orgs.
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 50);
      
    } catch (error: any) {
      console.error('Error switching organization:', error);
      toast({
        title: 'Erro ao trocar organização',
        description: error.message || 'Ocorreu um erro inesperado',
        variant: 'destructive',
      });
      throw error;
    }
  }, [availableOrgs, currentOrgId, queryClient]);

  const refetchOrganizations = useCallback(async () => {
    await fetchAvailableOrganizations();
    await loadCurrentOrganization();
  }, [fetchAvailableOrganizations, loadCurrentOrganization]);

  // Usar ref para estabilizar loadCurrentOrganization e evitar re-execuções do useEffect
  const loadCurrentOrganizationRef = useRef(loadCurrentOrganization);
  useEffect(() => {
    loadCurrentOrganizationRef.current = loadCurrentOrganization;
  }, [loadCurrentOrganization]);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout;
    let hasInitialized = false;
    
    // Verificar sessão primeiro (mais rápido - lê do localStorage)
    const initOrganization = async () => {
      // Evitar múltiplas inicializações
      if (hasInitialized) {
        return;
      }
      hasInitialized = true;
      
      try {
        // getSession() é muito rápido porque lê do localStorage primeiro
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        
        // Se não há sessão, não precisa carregar organizações
        if (sessionError || !sessionData?.session?.user) {
          if (mounted) {
            setLoading(false);
            setCurrentOrgId(null);
            setCurrentOrg(null);
            setAvailableOrgs([]);
          }
          return;
        }

        // Timeout de segurança - se não carregar em 10 segundos, parar loading
        timeoutId = setTimeout(() => {
          if (mounted) {
            console.warn('Timeout ao carregar organização - parando loading');
            setLoading(false);
          }
        }, 10000);

        // Se há sessão, carregar organizações em paralelo
        // Não bloquear renderização - carregar em background
        await loadCurrentOrganizationRef.current();
        
        if (mounted && timeoutId) {
          clearTimeout(timeoutId);
        }
      } catch (error) {
        console.error('Error initializing organization:', error);
        if (mounted) {
          setLoading(false);
          if (timeoutId) clearTimeout(timeoutId);
        }
      }
    };

    // Executar imediatamente
    initOrganization();

    // Também escutar mudanças de autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (mounted) {
        logDiag('ORG_CTX', `onAuthStateChange: ${event} | hasInitialized=${hasInitializedRef.current}`);
        if (event === 'SIGNED_OUT') {
          // Token refresh do Supabase dispara SIGNED_OUT → SIGNED_IN em ~60s.
          // Org não muda durante token refresh — preservar estado para evitar disruption.
          // Logout real sempre navega via window.location.href → full page reload.
          logDiag('ORG_CTX', 'SIGNED_OUT ignorado — org state preservado (token refresh)');
        } else if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
          // Se já inicializado, IGNORAR silenciosamente.
          // O Supabase dispara SIGNED_IN periodicamente (~60s) no token refresh.
          // Recarregar org aqui causa loading:true → PrivateRoute desmonta children.
          if (hasInitializedRef.current) {
            return;
          }
          // Primeira vez: carregar org normalmente
          loadCurrentOrganizationRef.current();
        }
        // TOKEN_REFRESHED: ignorado (não precisa recarregar org)
      }
    });

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []); // Remover loadCurrentOrganization das dependências, usar ref

  // Memoizar o value para evitar re-renders desnecessários
  const value: OrganizationContextType = useMemo(() => ({
    currentOrgId,
    currentOrg,
    availableOrgs,
    switchOrganization,
    loading,
    error,
    refetchOrganizations,
  }), [currentOrgId, currentOrg, availableOrgs, switchOrganization, loading, error, refetchOrganizations]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};